<template>
    <b-form-group
        :label="label"
        :label-class="labelClass"
        :description="description"
        :invalid-feedback="error"
        :state="state"
        class="form-group-dropdown"
        v-click-outside="closeDropDown"
        ref="form_dropdown"
    >
        <b-input-group class="flex-nowrap">
            <div v-if="multiSelect" class="d-flex position-absolute selected-items-wrapper">
                <div v-for="option in selectedOptions" :key="option.value" class="mr-2 dropdown-selected-item-wrapper">
                    <div class="d-flex align-items-center dropdown-selected-item text-primary">
                        <span class="d-block mr-2">{{ option.text }}</span>
                        <span class="d-flex" @click="toggleOption(option.value, false)">
                            <font-awesome-icon class="cursor-pointer" :icon="['fas', 'xmark']" />
                        </span>
                    </div>
                </div>
                <div v-if="isMobile ? value.length > 1 : value.length > 3" class="mr-2 dropdown-selected-item-wrapper">
                    <div class="d-flex align-items-center dropdown-selected-item text-primary">
                        <span class="d-block mr-2">+{{ selectedOptionsCount - selectedOptions.length }}</span>
                    </div>
                </div>
            </div>
            <div @click="handleRightButtonClick" class="input-wrapper">
                <b-form-input
                    :value="valueLabel"
                    :state="state"
                    :placeholder="this.value && this.value.length ? '' : placeholder || 'Select One'"
                    :class="['rounded-right', 'ldx-form-input']"
                    :disabled="true"
                >
                </b-form-input>
                <div class="placeholder"></div>
            </div>
            <b-input-group-append>
                <b-button
                    @click="handleRightButtonClick"
                    variant="primary"
                    class="dropdown-button"
                    :disabled="disabled"
                >
                    <font-awesome-icon :icon="['fas', 'chevron-down']"></font-awesome-icon>
                </b-button>
            </b-input-group-append>
        </b-input-group>
        <ul class="dropdown-options" v-if="isOpen">
            <b-form-input
                v-if="isSearch"
                :state="state"
                v-model="search"
                :placeholder="'Search'"
                :class="['ldx-form-input', 'ldx-form-input-search', 'm-2']"
            />
            <div class="w-100" v-if="isGrouped">
                <div class="dropdown-option-group" v-for="group in Object.keys(filteredGroupedOptions)" :key="group">
                    <span class="dropdown-option-group-label">{{ group }}</span>
                    <li
                        class="dropdown-option pl-4"
                        v-for="option in filteredGroupedOptions[group]"
                        :key="option.value"
                        @click="multiSelect ? toggleOption(option.value) : onSelect(option)"
                    >
                        <form-checkbox
                            v-if="multiSelect"
                            v-model="option.selected"
                            class="mb-0"
                            @change="(val) => toggleOption(option.value)"
                            >{{ option.text }}</form-checkbox
                        >
                        <span v-else>{{ option.text }}</span>
                    </li>
                </div>
            </div>

            <div v-else className="w-100">
                <li
                    class="dropdown-option"
                    v-for="option in filteredOptions"
                    :key="option.value"
                    @click="multiSelect ? toggleOption(option.value) : onSelect(option)"
                >
                    <form-checkbox
                        v-if="multiSelect"
                        v-model="option.selected"
                        class="mb-0"
                        @change="(val) => toggleOption(option.value)"
                        >{{ option.text }}</form-checkbox
                    >
                    <span v-else>{{ option.text }}</span>
                </li>
            </div>
        </ul>
    </b-form-group>
</template>

<script>
    import { formFieldMixin } from '../../../mixins/form-field-mixin';
    import FormInputGroup from '../form-input-group/form-input-group.vue';
    import FormCheckbox from '../form-checkbox/form-checkbox.vue';
    import groupBy from 'lodash/groupBy';

    export default {
        name: 'FormDropdown',
        props: {
            value: [String, Array, Number, Boolean],
            label: [String],
            labelClass: {
                type: Array,
                default: () => [],
            },
            placeholder: {
                type: String,
                default: '',
            },
            options: {
                type: Array,
                default: () => [],
            },
            multiSelect: {
                type: Boolean,
                default: false,
            },
            isSearch: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            isGrouped: {
                type: Boolean,
                default: false,
            },
            error: {
                type: String,
                default: null,
            },
            scrollContainer: {
                type: String,
                default: '.lx-page-container',
            },
        },
        data() {
            return {
                isOpen: false,
                isChildrenOpen: false,
                internalOptions: [],
                search: '',
                windowWidth: 0,
                container: null,
            };
        },
        mixins: [formFieldMixin],
        components: {
            FormInputGroup,
            FormCheckbox,
        },
        computed: {
            valueLabel() {
                return this.internalOptions && this.internalOptions.find((item) => item.value === this.value)?.text;
            },
            filteredOptions() {
                return this.internalOptions.filter(
                    (option) => option.text.toLowerCase().indexOf(this.search.toLowerCase()) != -1
                );
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md;
            },
            selectedOptions() {
                return this.internalOptions.filter((item) => item.selected).splice(0, this.isMobile ? 1 : 3);
            },
            selectedOptionsCount() {
                return this.internalOptions.filter((item) => item.selected).length;
            },
            filteredGroupedOptions() {
                return groupBy(this.filteredOptions, 'group');
            },
            state() {
                if (this.error) {
                    return !this.error;
                }
            },
        },
        mounted() {
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        unmounted() {
            window.removeEventListener('resize', this.onResize);
        },
        methods: {
            handleRightButtonClick(event) {
                if (!this.disabled) {
                    this.isOpen = !this.isOpen;
                    if (this.isOpen) {
                        this.$refs.form_dropdown.$el.scrollIntoView({
                            block: 'center',
                            behavior: 'smooth',
                        });
                    }
                    this.$emit('handle-right-button-click');
                }
            },
            toggleOption(value = '') {
                const option = this.internalOptions.find((option) => option.value === value);

                if (option) {
                    option.selected = !option.selected;
                    this.$emit('input', [
                        ...this.internalOptions.filter((option) => option.selected).map((option) => option.value),
                    ]);
                }
            },
            onSelect(option) {
                if (!this.multiSelect) {
                    this.isOpen = !this.isOpen;
                    this.$emit('input', option.value);
                }
            },
            closeDropDown() {
                this.isOpen = false;
            },
            openDropDown() {
                this.isOpen = true;
            },
            onResize() {
                this.windowWidth = window.innerWidth;
            },
        },
        watch: {
            options: {
                immediate: true,
                handler(options) {
                    this.internalOptions = [...options].map((option) => ({
                        ...option,
                        selected: this.multiSelect
                            ? this.value && this.value.includes(option.value)
                            : option.value === this.value,
                    }));
                },
            },
            value: {
                immediate: true,
                handler(value) {
                    this.internalOptions = this.internalOptions.map((option) => ({
                        ...option,
                        selected: this.multiSelect ? value && value.includes(option.value) : option.value === value,
                    }));
                },
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    $selected-button-color: #08c;
    $primary-color: #0d47a1;
    $scroll-background-color: #f5f5f5;
    $border-color: #d8d8d8;
    $font-color: #fff;

    @mixin scroll-bar() {
        scrollbar-color: $primary-color $scroll-background-color;

        &::-webkit-scrollbar-track {
            -webkit-box-shadow: inset 0 0 6px $border-color;
            border-radius: 10px;
            background-color: $scroll-background-color;
            border-right-width: 1px;
            border-left-width: 1px;
        }

        &::-webkit-scrollbar {
            width: 8px;
            height: 6px;
            background-color: $scroll-background-color;
            border-right-width: 1px;
            border-left-width: 1px;
        }

        &::-webkit-scrollbar-thumb {
            border-radius: 10px;
            background-color: $primary-color;
            -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        }
    }

    .ldx-form-input {
        border: none;
        outline: none;
        box-shadow: none;
        background: #f1f1f1;
        border-radius: 5px;
    }

    .input-wrapper {
        position: relative;
        width: 100%;
        .placeholder {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
            z-index: 2;
        }
    }

    .ldx-form-input-search {
        background: #fff !important;
        border-radius: 0 !important;
        width: 98% !important;
    }

    .dropdown-button {
        border: none !important;
        box-shadow: none !important;
    }

    .is-invalid {
        input.rounded-right {
            border: 1px solid #dc3545 !important;
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }
    }

    .form-group-dropdown {
        position: relative;
    }

    .dropdown-selected-item-wrapper {
        z-index: 2;
    }

    .selected-items-wrapper {
        z-index: 10;
    }

    .dropdown-selected-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 5px;
        background: rgba(13, 71, 161, 0.1);
        border: 1px solid #0d47a1;
        border-radius: 6px;
        @media (max-width: 768px) {
            span {
                max-width: 160px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        }
    }

    .dropdown-options {
        background: #f1f1f1;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 5px;
        position: absolute;
        width: 100%;
        top: 40px;
        z-index: 200;
        padding: 0;
        list-style: none;
        margin: 0;
        scrollbar-width: thin;
        overflow-y: auto;
        max-height: 30vh;
        @include scroll-bar;

        .dropdown-option {
            padding: 10px;
            cursor: pointer;
            transition: 0.3s;
            &:hover {
                background: #fff;
            }
        }
        .dropdown-option-group-label {
            padding: 10px;
            font-weight: bold;
            border-bottom: 1px solid #cdcdcd;
            display: block;
            width: 100%;
            font-size: 15px;
        }
        @media (max-width: 768px) {
            max-height: 25vh;
        }
    }
    .dropdown-options-up {
        top: auto !important;
        bottom: 110%;
    }
</style>
